import React, { useEffect, useMemo, useState, useRef } from "react";
import AutocompleteInput from './AutocompleteInput';
import { LabExam } from "../../LabExam";
import "./Filter.css";

interface FilterProps {
  data: LabExam[];
  onFilterChange: (filters: {
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
    weldingJointType: string[];
  }) => void;
  examResults: string[];
  onExamResultChange: (results: string[]) => void;
  onRegionSelect?: (region: string) => void;
  selectedRegions?: string[];
  onResetFilters?: () => void;
  initialFilters?: Partial<{
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
    weldingJointType: string[];
  }>;
  open?: boolean;
  showWeldingJointType?: boolean;
}

function Filter({ data, onFilterChange, examResults, onExamResultChange, onRegionSelect, selectedRegions, onResetFilters, initialFilters, open, showWeldingJointType = false }: FilterProps) {
  const ensureArray = (v: any) => (Array.isArray(v) ? v : []);
  const [filters, setFilters] = useState(() => ({
    manufacturerName: ensureArray(initialFilters?.manufacturerName) as string[],
    manufacturerInn: ensureArray(initialFilters?.manufacturerInn) as string[],
    region: ensureArray(initialFilters?.region) as string[],
    objectAddress: ensureArray(initialFilters?.objectAddress) as string[],
    weldingJointType: ensureArray(initialFilters?.weldingJointType) as string[],
  }));

  const [inputValues, setInputValues] = useState({
    manufacturerName: "",
    manufacturerInn: "",
    region: "",
    objectAddress: "",
    weldingJointType: "",
  });

  const [uniqueValues, setUniqueValues] = useState({
    manufacturerNames: [] as string[],
    manufacturerInns: [] as string[],
    regions: [] as string[],
    objectAddresses: [] as string[],
    weldingJointTypes: [] as string[],
    examResults: [] as string[],
  });

  const fieldToDataMapping = useMemo(() => ({
    objectAddress: 'object_adres',
    manufacturerName: 'name_company',
    manufacturerInn: 'inn_company',
    region: 'region_company',
    weldingJointType: 'welding_joint_type'
  }), []);

  const initialMount = useRef(true);

  const initialFiltersKey = JSON.stringify(initialFilters || {});

  useEffect(() => {
    if (!open || !initialFilters) return;
    const ensureArray = (v: any) => (Array.isArray(v) ? v : []);
    const next = {
      manufacturerName: ensureArray(initialFilters.manufacturerName),
      manufacturerInn: ensureArray(initialFilters.manufacturerInn),
      region: ensureArray(initialFilters.region),
      objectAddress: ensureArray(initialFilters.objectAddress),
      weldingJointType: ensureArray(initialFilters.weldingJointType),
    };
    const isSame =
      next.manufacturerName.join('|') === filters.manufacturerName.join('|') &&
      next.manufacturerInn.join('|') === filters.manufacturerInn.join('|') &&
      next.region.join('|') === filters.region.join('|') &&
      next.objectAddress.join('|') === filters.objectAddress.join('|') &&
      next.weldingJointType.join('|') === filters.weldingJointType.join('|');
    if (!isSame) {
      setFilters(next);
    }
  }, [open, initialFiltersKey]);

  useEffect(() => {
    const names = Array.from(new Set(
      data
        .filter(item => item.name_company != null)
        .map(item => item.name_company)
    )).sort();

    const weldingJointTypes = Array.from(new Set(
      data.map(item => item.welding_joint_type).filter((value): value is string => !!value?.trim())
    )).sort();
    
    const inns = Array.from(new Set(
      data
        .filter(item => item.inn_company != null)
        .map(item => item.inn_company)
    )).sort();
    
    const regions = Array.from(new Set(
      data
        .filter(item => item.region_company != null)
        .map(item => item.region_company)
    )).sort();
    
    const addresses = Array.from(new Set(
      data
        .filter(item => item.object_adres != null)
        .map(item => item.object_adres)
    )).sort();
    
    const results = Array.from(new Set(
      data
        .filter(item => item.exam_result != null)
        .map(item => item.exam_result)
    )).sort();

    setUniqueValues({
      manufacturerNames: names,
      manufacturerInns: inns,
      regions: regions,
      objectAddresses: addresses,
      weldingJointTypes,
      examResults: results,
    });
  }, [data]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    if (selectedRegions && selectedRegions.length > 0) {
      const newRegions = selectedRegions.filter(region => 
        !filters.region.includes(region)
      );
      
      if (newRegions.length > 0) {
        setFilters(prev => ({
          ...prev,
          region: [...prev.region, ...newRegions]
        }));
      }
    } else if (filters.region.length > 0) {
      setFilters(prev => ({
        ...prev,
        region: []
      }));
    }
  }, [selectedRegions]);

  useEffect(() => {
    onFilterChange({
      manufacturerName: filters.manufacturerName,
      manufacturerInn: filters.manufacturerInn,
      region: filters.region,
      objectAddress: filters.objectAddress,
      weldingJointType: filters.weldingJointType,
    });
  }, [filters, onFilterChange]);

  const handleRegionSelect = (region: string) => {
    if (!region || !onRegionSelect) return;
    onRegionSelect(region);
  };

  const handleAddFilter = (field: keyof typeof filters, value: string) => {
    if (!value) return;
    
    if (!filters[field].includes(value)) {
      setFilters(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
      setInputValues(prev => ({
        ...prev,
        [field]: ""
      }));
       
      if (field === 'region' && onRegionSelect) {
        onRegionSelect(value);
      }
    }
  };

  const handleRemoveFilter = (field: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: prev[field].filter(v => v !== value)
    }));
    
    if (field === 'region' && onRegionSelect) {
      onRegionSelect(value);
    }
  };

  const resetFilters = () => {
    setFilters({
      manufacturerName: [],
      manufacturerInn: [],
      region: [],
      objectAddress: [],
      weldingJointType: [],
    });
    setInputValues({
      manufacturerName: "",
      manufacturerInn: "",
      region: "",
      objectAddress: "",
      weldingJointType: "",
    });
    onExamResultChange([]);
    
    if (onResetFilters) {
      onResetFilters();
    }
  };

  const activeFiltersCount = Object.values(filters).reduce((acc, values) => acc + values.length, 0) + examResults.length;

  const renderMultiSelect = (name: keyof typeof filters, label: string, options: string[]) => {
    const safeOptions = options || []; 
    
    return (
      <div className="filter-field">
        <label htmlFor={name}>{label}</label>
        <div className="multi-select-container">
          <AutocompleteInput
            id={String(name)}
            value={inputValues[name]}
            options={safeOptions}
            selectedValues={filters[name]}
            placeholder={`Выберите (${safeOptions.length})`}
            onChange={(val) => handleInputChange(name, val)}
            onSelectOption={(val) => {
              handleAddFilter(name, val);
              if (name === 'region' && onRegionSelect) onRegionSelect(val);
            }}
          />
        </div>
      </div>
    );
  };

  const getFilterLabel = (field: keyof typeof filters) => {
    switch (field) {
      case 'manufacturerName':
        return 'Наименование';
      case 'manufacturerInn':
        return 'ИНН';
      case 'region':
        return 'Местонахождение';
      case 'objectAddress':
        return 'Адрес';
      case 'weldingJointType':
        return 'Тип сварного соединения';
      default:
        return '';
    }
  };

  const cleanResultText = (text: string) => {
    return text
      .replace('(брак)', '')
      .replace('(фальсификат)', '')
      .trim();
  };

  const handleInputChange = (field: keyof typeof inputValues, value: string) => {
    setInputValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelect = (field: keyof typeof filters, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) return;
    
    const options = uniqueValues[`${field}s` as keyof typeof uniqueValues] as string[];
    
    if (options && options.includes(value)) {
      handleAddFilter(field, value);
      
      if (field === 'region' && onRegionSelect) {
        onRegionSelect(value);
      }
    }
  };

  const handleKeyDown = (field: keyof typeof filters, e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValues[field]) {
      const value = inputValues[field];
      if (!value) return;
      
      handleAddFilter(field, value);
      
      if (field === 'region' && onRegionSelect) {
        onRegionSelect(value);
      }
    }
  };

  const handleExamResultChange = (result: string) => {
    const newResults = examResults.includes(result)
      ? examResults.filter(r => r !== result)
      : [...examResults, result];
    onExamResultChange(newResults);
  };

  return (
    <div className="filter-container">
      <div className="filter-header">
        <h3>Фильтры {activeFiltersCount > 0 && <span className="filter-count">({activeFiltersCount})</span>}</h3>
        <button className="reset-button" onClick={resetFilters}>
          Сбросить
        </button>
      </div>
      
      {activeFiltersCount > 0 && (
        <div className="active-filters">
          <h4>Активные фильтры:</h4>
          {Object.entries(filters).map(([field, values]) => {
            return values.map(value => (
              <div key={`${field}-${value}`} className="active-filter-item">
                <span data-label={getFilterLabel(field as keyof typeof filters)}>{value}</span>
                <button 
                  className="remove-filter-button"
                  onClick={() => handleRemoveFilter(field as keyof typeof filters, value)}
                >
                  ×
                </button>
              </div>
            ));
          })}
          {examResults.map(result => (
            <div key={result} className="active-filter-item">
              <span data-label="Результат испытания">{cleanResultText(result)}</span>
              <button 
                className="remove-filter-button"
                onClick={() => handleExamResultChange(result)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div className="filter-fields">
        <div className="filter-group">                  
          {renderMultiSelect('manufacturerName', 'Наименование производителя', uniqueValues.manufacturerNames)}
          {renderMultiSelect('manufacturerInn', 'ИНН производителя', uniqueValues.manufacturerInns)}
          {renderMultiSelect('region', 'Местонахождение производителя', uniqueValues.regions)}
          {renderMultiSelect('objectAddress', 'Адрес объекта', uniqueValues.objectAddresses)}
          {showWeldingJointType && renderMultiSelect('weldingJointType', 'Тип сварного соединения', uniqueValues.weldingJointTypes)}
        </div>
      </div>
    </div>
  );
}

export default Filter;
